import axios from "axios";
import React, { useMemo, useState } from "react";
import { AlertTriangle, FileUp, Send, X } from "lucide-react";
import { createPortal } from "react-dom";

const API = process.env.REACT_APP_BACKEND_URL || "";
const MAX_FILES = 3;
const MAX_BYTES = 10 * 1024 * 1024;

const CATEGORIES = [
  ["bug", "Bug o malfunzionamento"],
  ["serious-behavior", "Atteggiamenti gravi o minacce"],
  ["privacy", "Violazione della privacy"],
  ["security", "Problema di sicurezza"],
  ["access", "Difficoltà di accesso o utilizzo"],
  ["suggestion", "Suggerimento"],
  ["other", "Altro"],
];

export function GeneralReportDialog({ onClose }) {
  const [category, setCategory] = useState("bug");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [state, setState] = useState("editing");
  const [error, setError] = useState("");
  const [reportId, setReportId] = useState("");
  const fileSummary = useMemo(() => files.map((file) => file.name).join(", "), [files]);

  const chooseFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    if (selected.length > MAX_FILES) {
      setError("Puoi allegare al massimo 3 file.");
      event.target.value = "";
      return;
    }
    if (selected.some((file) => file.size > MAX_BYTES)) {
      setError("Ogni allegato può pesare massimo 10 MB.");
      event.target.value = "";
      return;
    }
    setError("");
    setFiles(selected);
  };

  const submit = async (event) => {
    event.preventDefault();
    const note = description.trim();
    if (note.length < 10) {
      setError("Descrivi il problema con almeno 10 caratteri.");
      return;
    }
    setState("sending");
    setError("");
    const body = new FormData();
    body.append("category", category);
    body.append("description", note);
    body.append("page_url", window.location.href);
    files.forEach((file) => body.append("files", file));
    try {
      const response = await axios.post(`${API}/api/v1/general-reports`, body, { timeout: 45000 });
      setReportId(response.data?.id || "");
      setState("sent");
    } catch (err) {
      setError(err?.response?.data?.detail || "Invio non riuscito. Riprova tra poco.");
      setState("editing");
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-stretch sm:items-center justify-center bg-ink/60 p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="general-report-title">
      <section className="relative flex h-[100dvh] w-full max-w-xl flex-col overflow-hidden rounded-none border-t-4 border-t-primary bg-cream shadow-2xl sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:rounded-3xl">
        <header className="flex items-center gap-3 border-b border-edge bg-white px-4 py-4">
          <AlertTriangle className="text-primary" />
          <div><p className="text-[11px] font-bold uppercase tracking-[0.15em] text-primary">Assistenza e sicurezza</p><h2 id="general-report-title" className="text-xl font-bold">Segnala un problema</h2></div>
          <button type="button" onClick={onClose} className="ml-auto flex h-10 w-10 items-center justify-center rounded-full border border-edge" aria-label="Chiudi"><X size={19} /></button>
        </header>
        {state === "sent" ? (
          <div className="flex-1 p-6 text-center sm:flex-none">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"><Send /></div>
            <h3 className="text-2xl">Segnalazione trasmessa</h3>
            <p className="mt-2 text-sm text-muted">È stata registrata nel pannello amministratore e notificata all’indirizzo di gestione.</p>
            {reportId && <p className="mt-3 break-all text-xs text-muted">Codice: {reportId}</p>}
            <button type="button" onClick={onClose} className="big-cta mt-6">Chiudi</button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
              <label className="block text-sm font-semibold">Tipo di segnalazione
                <select value={category} onChange={(event) => setCategory(event.target.value)} className="mt-2 w-full rounded-xl border border-edge bg-white px-3 py-3">
                  {CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label className="block text-sm font-semibold">Descrizione
                <textarea value={description} onChange={(event) => setDescription(event.target.value)} minLength={10} maxLength={2000} rows={7} required className="mt-2 w-full resize-y rounded-xl border border-edge bg-white px-3 py-3 text-sm" placeholder="Spiega cosa è successo, dove e quando. Non inserire password o le 12 parole di recupero." />
                <span className="mt-1 block text-right text-xs text-muted">{description.length}/2000</span>
              </label>
              <label className="block rounded-2xl border-2 border-dashed border-edge bg-white p-4 text-sm">
                <span className="flex items-center gap-2 font-semibold"><FileUp size={18} /> Allega file</span>
                <span className="mt-1 block text-xs text-muted">Massimo 3 file, 10 MB ciascuno: immagini, MP4/WebM, PDF o testo.</span>
                <input type="file" multiple accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,application/pdf,text/plain" onChange={chooseFiles} className="mt-3 block w-full text-xs" />
                {fileSummary && <span className="mt-2 block break-words text-xs">{fileSummary}</span>}
              </label>
              {error && <p className="rounded-xl bg-red-50 p-3 text-sm text-primary" role="alert">{error}</p>}
            </div>
            <footer className="border-t border-edge bg-white p-4"><button type="submit" disabled={state === "sending"} className="big-cta flex items-center justify-center gap-2 disabled:opacity-50"><Send size={18} />{state === "sending" ? "Trasmissione…" : "Trasmetti segnalazione"}</button></footer>
          </form>
        )}
      </section>
    </div>,
    document.body,
  );
}
